import axiosClient from "../shared/api/axios-client";
import {
  type ModuleProgressionMode,
  parseModuleProgressionMode,
} from "@/domain/moduleProgressionMode";

/** React Query key for module completion status */
export const moduleCompletionQueryKey = (moduleId: string) =>
  ["module-completion", moduleId] as const;

/** Raw JSON from GET /api/modules/{moduleId}/completion-status. */
interface ModuleCompletionStatusJson {
  moduleId: string;
  isCompleted: boolean;
  isApproved: boolean;
  totalExercises: number;
  answeredExercises: number;
  correctAnswers: number;
  scorePercentage: number;
  progressionMode: unknown;
  nextModuleId: string | null;
  isNextModuleAvailable: boolean;
}

/**
 * Response from GET /api/modules/{moduleId}/completion-status
 * ({@link progressionMode} is always a {@link ModuleProgressionMode} enum after parsing.)
 */
export interface ModuleCompletionStatusResponse {
  moduleId: string;
  isCompleted: boolean;
  isApproved: boolean;
  totalExercises: number;
  answeredExercises: number;
  correctAnswers: number;
  scorePercentage: number;
  /** How the package gates the next module after content work: mastery-by-content vs pool-based (see openspec/config.yaml glossary). */
  progressionMode: ModuleProgressionMode;
  nextModuleId: string | null;
  isNextModuleAvailable: boolean;
}

/**
 * Get module completion status for current student
 * GET /api/modules/{moduleId}/completion-status
 */
export const apiGetModuleCompletionStatus = async (
  moduleId: string
): Promise<ModuleCompletionStatusResponse> => {
  try {
    const response = await axiosClient.get<ModuleCompletionStatusJson>(
      `/modules/${moduleId}/completion-status`
    );
    const d = response.data;
    return {
      moduleId: d.moduleId,
      isCompleted: d.isCompleted,
      isApproved: d.isApproved,
      totalExercises: d.totalExercises,
      answeredExercises: d.answeredExercises,
      correctAnswers: d.correctAnswers,
      scorePercentage: d.scorePercentage,
      progressionMode: parseModuleProgressionMode(d.progressionMode),
      nextModuleId: d.nextModuleId,
      isNextModuleAvailable: d.isNextModuleAvailable,
    };
  } catch (error) {
    console.error("Error fetching module completion status:", error);
    throw error;
  }
};

export interface RetryModuleContentExercisesResponse {
  retryPrepared: boolean;
  clearedAnswerCount: number;
}

/**
 * Prepare retry for content exercises below approval threshold.
 * POST /api/modules/{moduleId}/retry-content-exercises
 */
export const apiRetryModuleContentExercises = async (
  moduleId: string
): Promise<RetryModuleContentExercisesResponse> => {
  const response = await axiosClient.post<{
    retryPrepared: boolean;
    clearedAnswerCount: number;
  }>(`/modules/${moduleId}/retry-content-exercises`);
  return {
    retryPrepared: response.data.retryPrepared,
    clearedAnswerCount: response.data.clearedAnswerCount,
  };
};
